// src/pages/ModelOptionsPage.tsx
import React, { useEffect, useState } from "react";
import {
  Box, Button, Card, CardContent, Container, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Typography,
  Divider, Pagination, MenuItem, FormControl, InputLabel,
  Select, Alert, CardActions, Snackbar, // Ajout de Snackbar
} from "@mui/material";
import Image from '@mui/icons-material/Image';
import { Add, Edit, Delete } from "@mui/icons-material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
const API_URL = process.env.REACT_APP_API_URL;
const API_IMAGE = process.env.REACT_APP_Image;

const API =    `${API_URL}/options-modele`;
const API_MODELS = `${API_URL}/modeles`;

const theme = createTheme({
  palette: {
    mode: "light",
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    primary: { main: "#C4B08C" },
    error: { main: "#d32f2f" },
    text: { primary: "#111", secondary: "#555" },
  },
  typography: { fontFamily: '"Inter", sans-serif' },
});

interface Option {
  id: number;
  option: string;
  description: string;
  image: string;
  imageUrl?: string;
  idModele: number;
}

interface Model {
  id: number;
  modele: string;
}

const rowsPerPage = 6;

const ModelOptionsPage = () => {
  const [options, setOptions] = useState<Option[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [filter, setFilter] = useState("");
  const [filterModel, setFilterModel] = useState<number | "all">("all");
  const [page, setPage] = useState(1);
  const [modal, setModal] = useState<{ type: "add" | "edit"; data: Partial<Option> } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" as "success" | "error" });
  
  // États pour la confirmation de suppression
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  });

  /* ---- Fetch ---- */
  const fetchOptions = async () => {
    const res = await fetch(API);
    const data = await res.json();
    console.log(data)
    setOptions(data);
  };
  const fetchModels = async () => {
    const res = await fetch(`${API_URL}/modeles`);
    const data = await res.json();
    setModels(data);
  };
  useEffect(() => {
    fetchOptions();
    fetchModels();
  }, []);

  /* ---- CRUD ---- */
  const handleSave = async () => {
    if (!modal) return;
    
    // Contrôle de saisie
    if (!modal.data.option || !modal.data.description || !modal.data.idModele) {
      setSnack({ 
        open: true, 
        message: "Please fill in all required fields", 
        severity: "error" 
      });
      return;
    }

    const url = modal.data.id ? `${API}/${modal.data.id}` : API;
    const method = modal.data.id ? "PUT" : "POST";

    try {
      const formData = new FormData();

      formData.append("option", modal.data.option || "");
      formData.append("description", modal.data.description || "");
      formData.append("idModele", String(modal.data.idModele || ""));

      if (file) {
        formData.append("image", file);
      }

      const response = await fetch(url, {
        method,
        body: formData,
      });

      if (!response.ok) throw new Error("Save failed");

      const data = await response.json();

      setModal(null);
      fetchOptions();
      setSnack({ open: true, message: "Saved successfully", severity: "success" });
      setFile(null);
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Save failed", severity: "error" });
    }
  };

  // Gestion de la suppression avec boîte de dialogue
  const handleDeleteClick = (id: number) => {
    setDeleteDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    if (deleteDialog.id === null) return;
    
    try {
      const response = await fetch(`${API}/${deleteDialog.id}`, { method: "DELETE" });
      
      if (!response.ok) throw new Error("Delete failed");
      
      setSnack({ open: true, message: "Deleted successfully", severity: "success" });
      fetchOptions();
    } catch (err) {
      console.error(err);
      setSnack({ open: true, message: "Delete failed", severity: "error" });
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, id: null });
  };

  /* ---- filtering ---- */
  const filtered = options.filter(
    (o) =>
      (filterModel === "all" || o.idModele === filterModel) &&
      (o.option.toLowerCase().includes(filter.toLowerCase()) ||
        o.description.toLowerCase().includes(filter.toLowerCase()))
  );
  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Snackbar pour les messages */}
        <Snackbar
          open={snack.open}
          autoHideDuration={6000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnack({ ...snack, open: false })} 
            severity={snack.severity}
            sx={{ width: '100%' }}
          >
            {snack.message}
          </Alert>
        </Snackbar>

        <Typography variant="h4" fontWeight={700} mb={3}>
          🚤 Model Options
        </Typography>

        {/* Filters & Add */}
        <Box display="flex" gap={2} flexWrap="wrap" mb={3} alignItems="center">
          <TextField
            placeholder="Search options..."
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ flex: 1, bgcolor: "#f5f5f5", borderRadius: 2 }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Model</InputLabel>
            <Select
              value={filterModel}
              onChange={(e) => setFilterModel(e.target.value as number | "all")}
            >
              <MenuItem value="all">All</MenuItem>
              {models.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.modele}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ bgcolor: "primary.main", color: "#000" }}
            onClick={() =>
              setModal({
                type: "add",
                data: { option: "", description: "", idModele: models[0]?.id },
              })
            }
          >
            Add Option
          </Button>
        </Box>

        {/* Cards grouped by model */}
        {models.map((m) => {
          const opts = filtered.filter((o) => o.idModele === m.id);
          if (opts.length === 0) return null;
          return (
            <Box key={m.id} mb={6}>
              <Typography variant="h5" fontWeight={700} color="primary" mb={2}>
                {m.modele}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box display="flex" flexWrap="wrap" gap={2}>
                {opts.map((opt) => (
                  <Card
                    key={opt.id}
                    sx={{
                      flex: "1 1 250px",
                      borderRadius: 3,
                      boxShadow: "0 4px 16px rgba(0,0,0,.06)",
                      transition: "all .3s",
                      "&:hover": { transform: "translateY(-4px)" },
                    }}
                  >
                    <img
                      src={
                        opt.image ||
                        `${API_URL}/images/options-modeles/${opt.image}`
                      }
                      alt={opt.option}
                      style={{
                        width: "100%",
                        height: 140,
                        objectFit: "cover",
                        borderTopLeftRadius: 12,
                        borderTopRightRadius: 12,
                      }}
                    />
                    <CardContent>
                      <Typography variant="h6">{opt.option}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </CardContent>
                    <Divider />
                    <CardActions sx={{ justifyContent: "space-between" }}>
                      <Button
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => setModal({ type: "edit", data: opt })}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        startIcon={<Delete />}
                        onClick={() => handleDeleteClick(opt.id)}
                      >
                        Delete
                      </Button>
                    </CardActions>
                  </Card>
                ))}
              </Box>
            </Box>
          );
        })}

        <Pagination
          count={Math.ceil(filtered.length / rowsPerPage)}
          page={page}
          onChange={(_, v) => setPage(v)}
          color="primary"
          sx={{ mt: 4, display: "flex", justifyContent: "center" }}
        />

        {/* Modal (Add/Edit) */}
        {modal && (
          <Dialog
            open
            onClose={(event, reason) => {
              // Only close when the X button is clicked
              if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                setModal(null);
              }
            }}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 10px 30px rgba(0,0,0,0.1)" } }}
          >
            <DialogTitle sx={{ bgcolor: "#F0F0F0", borderBottom: "1px solid #E0E0E0", p: 2.5 }}>
              <Typography variant="h5" fontWeight={600} sx={{ color: theme.palette.text.primary }}>
                {modal.type === "add" ? "Add New Option" : "Edit Option"}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ py: 3, px: 4, display: "flex", flexDirection: "column", gap: 2.5 }}>
              <TextField
                label="Option Name"
                fullWidth
                value={modal.data.option || ""}
                onChange={(e) =>
                  setModal({
                    ...modal,
                    data: { ...modal.data, option: e.target.value },
                  })
                }
                sx={{ mb: 2, mt: 2}}
                required
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={modal.data.description || ""}
                onChange={(e) =>
                  setModal({
                    ...modal,
                    data: { ...modal.data, description: e.target.value },
                  })
                }
                required
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 3,
                  border: "2px dashed #B39D77",
                  borderRadius: 2,
                  bgcolor: "#fcfcfc",
                  transition: "all 0.3s",
                  "&:hover": { bgcolor: "#f5f5f5" },
                }}
              >
                {(file || modal.data.image) ? (
                  <img
                    src={file ? URL.createObjectURL(file) : `${API_URL}/images/options_modeles/${modal.data.id}.png`}
                    alt="preview"
                    style={{
                      width: "100%",
                      maxHeight: 250,
                      objectFit: "contain",
                      marginBottom: 16,
                      borderRadius: 8,
                    }}
                  />
                ) : (
                  <Box sx={{ color: "text.disabled", textAlign: "center", mb: 2 }}>
                    <Image sx={{ fontSize: 50, mb: 1 }} />
                    <Typography>Click or drag image to upload</Typography>
                  </Box>
                )}
                <Button
                  variant="contained"
                  component="label"
                >
                  {modal.data.image || file ? "Change Image" : "Upload Image"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </Button>
              </Box>
              <FormControl fullWidth required>
                <InputLabel>Model</InputLabel>
                <Select
                  value={modal.data.idModele || ""}
                  onChange={(e) =>
                    setModal({
                      ...modal,
                      data: { ...modal.data, idModele: e.target.value as number },
                    })
                  }
                  label="Model"
                >
                  {models.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.modele}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions sx={{ p: 3, borderTop: "1px solid #eee" }}>
              <Button onClick={() => setModal(null)} color="secondary">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
              >
                Save
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Boîte de dialogue de confirmation de suppression */}
        <Dialog
          open={deleteDialog.open}
          onClose={(event, reason) => {
            // Only close when the X button is clicked
            if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
              handleDeleteCancel();
            }
          }}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this option?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="secondary">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              color="error" 
              variant="contained"
              startIcon={<Delete />}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </ThemeProvider>
  );
};

export default ModelOptionsPage;
